import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";

const LINKS = [
  { hash: "services", label: "Services" },
  { hash: "about", label: "About" },
  { hash: "process", label: "Process" },
  { hash: "contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  // Build a link that scrolls in-page on the landing route, or navigates back
  // to the landing route (with hash) from anywhere else.
  const linkFor = (hash) => (isHome ? `#${hash}` : `/#${hash}`);
  const brandHref = isHome ? "#top" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <a
          href={brandHref}
          data-testid="brand-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="/brand/nexaverve-logo-dark.svg"
            alt="Nexaverve Solutions"
            className="h-9 sm:h-10 w-auto object-contain"
          />
          <span className="hidden md:flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 ml-1">
            <span className="w-1.5 h-1.5 bg-[#FBBF24] nx-pulse" />
            UK
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.hash}
              href={linkFor(l.hash)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={linkFor("contact")}
            data-testid="nav-cta-quote"
            className="hidden sm:inline-flex items-center gap-2 bg-[#FBBF24] hover:bg-[#F59E0B] text-black font-bold uppercase tracking-wider text-xs px-4 py-2.5 transition-colors"
          >
            Get a Quote <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-white/15 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-black/95 border-t border-white/10"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {LINKS.map((l) => (
              <a
                key={l.hash}
                href={linkFor(l.hash)}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="font-mono text-sm uppercase tracking-[0.2em] text-neutral-300 hover:text-[#FBBF24]"
              >
                {l.label}
              </a>
            ))}
            <a
              href={linkFor("contact")}
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-cta"
              className="nx-btn-primary mt-2 self-start"
            >
              Get a Quote <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
